import { EventBus } from '../core/events'
import { World } from '../ecs/world'
import type { Entity } from '../ecs/entity'
import { resetEntityIds } from '../ecs/entity'
import type { InputSource, Intent } from '../input/types'
import { emptyIntent } from '../input/types'
import type { Tuning } from '../config/tuning'
import { AUTHORED_LEVELS } from '../levels/authored'
import { loadLevel } from '../world/loader'
import { norm } from '../core/math'
import { playerSystem } from '../systems/player'
import { aiSystem } from '../systems/ai'
import { generatorSystem } from '../systems/generators'
import { projectileSystem } from '../systems/projectiles'
import { pickupSystem } from '../systems/pickups'
import { attritionSystem } from '../systems/attrition'
import { bossSystem } from '../systems/boss'
import { fxSystem, snapshotPositions } from '../systems/fx'

/**
 * ATTRACT MODE (issue #45): a real, self-playing simulation rendered dimmed
 * behind the main menu — in-fiction, the broadcast currently on air.
 *
 * Isolation is the whole design:
 * - The attract world runs on its OWN EventBus that nothing subscribes to.
 *   The real presentation stack (DAISE feed, achievements, run stats, the
 *   live-DAISE client, audio, scoring) hangs off the game's bus and therefore
 *   never hears a single attract event. Nothing here persists anywhere.
 * - The bot drives the player through the standard InputSource contract —
 *   game logic still reads intent, never "an AI flag".
 * - Cosmetic by definition: seeds come from wall clock like any unseeded run,
 *   and the loop restarts on death, clearance, floor exhaustion, or timeout.
 */

const hostile = (e: Entity): boolean => !e.dead && (e.kind === 'enemy' || e.kind === 'generator' || e.kind === 'boss')

/**
 * The bot: greedy target-nearest heuristics with wall-slide movement (the
 * collision system already slides along walls) and a perpendicular detour
 * when progress stalls. It does not need to be good — it needs to look alive.
 */
class AttractSource implements InputSource {
  world: World | null = null
  private t: Tuning['attract']
  private prevX = 0
  private prevY = 0
  private progressT = 0
  private detourUntil = 0
  private detourSign = 1
  private magicAt = 0

  constructor(t: Tuning['attract']) {
    this.t = t
  }

  poll(px: number, py: number): Intent {
    const i = emptyIntent()
    const w = this.world
    if (!w) return i
    const now = w.levelTime

    let target: Entity | null = null
    let bd = Infinity
    let near = 0
    for (const e of w.entities) {
      if (!hostile(e)) continue
      const d2 = (e.x - px) ** 2 + (e.y - py) ** 2
      if (d2 < bd) {
        bd = d2
        target = e
      }
      if (d2 < this.t.panicRadius * this.t.panicRadius) near++
    }

    if (target) {
      const dist = Math.sqrt(bd)
      const dir = norm({ x: target.x - px, y: target.y - py })
      i.aim = dir
      i.fire = dist < this.t.fireRange
      // hold a skirmish band: close when far, back off when crowded, strafe between
      if (dist > this.t.bandFar) i.move = dir
      else if (dist < this.t.bandNear) i.move = { x: -dir.x, y: -dir.y }
      else {
        const s = Math.sin(now * 1.7) >= 0 ? 1 : -1
        i.move = { x: -dir.y * s, y: dir.x * s }
      }
      // cornered: pop the field spell like a panicking participant would
      if (near >= this.t.panicCount && now - this.magicAt > this.t.magicCooldown) {
        this.magicAt = now
        i.magic = true
      }
    } else {
      // nothing left nearby — drift in a slow arc until the loop recycles
      i.move = { x: Math.cos(now * 0.45), y: Math.sin(now * 0.45) }
      i.aim = norm(i.move)
    }

    // stalled against geometry: detour perpendicular for a beat
    this.progressT += 1 / 60
    if (this.progressT >= 1.2) {
      const moved = Math.hypot(px - this.prevX, py - this.prevY)
      if (moved < 12 && (i.move.x !== 0 || i.move.y !== 0)) {
        this.detourUntil = now + 0.9
        this.detourSign = -this.detourSign
      }
      this.prevX = px
      this.prevY = py
      this.progressT = 0
    }
    if (now < this.detourUntil) {
      i.move = { x: -i.move.y * this.detourSign, y: i.move.x * this.detourSign }
    }
    return i
  }

  setClassicMode(): void {}
  clearQueued(): void {}
  destroy(): void {}
}

export class AttractMode {
  /** isolated bus — nothing ever subscribes; see the module docblock */
  private bus = new EventBus()
  private world: World | null = null
  private source: AttractSource
  private loopT = 0
  private deadT = 0
  private loopCount = 0

  constructor(private tuning: Tuning) {
    this.source = new AttractSource(tuning.attract)
  }

  /** the world to render behind the menu, or null when attract is off */
  current(): World | null {
    return this.world
  }

  start(): void {
    const t = this.tuning.attract
    const floor = t.floorPool[this.loopCount % t.floorPool.length]!
    this.loopCount++
    // wall-clock seed, exactly like an unseeded real run — cosmetic loop
    const seed = (Date.now() + this.loopCount * 7919) & 0x7fffffff
    resetEntityIds()
    const world = new World(this.bus, this.tuning, seed)
    world.mode = 'campaign'
    world.amenities = new Set()
    world.shakeEnabled = false // the background must never shake the menu
    const level = AUTHORED_LEVELS[floor - 1]!
    loadLevel(world, level, floor, [this.source])
    this.source.world = world
    this.world = world
    this.loopT = 0
    this.deadT = 0
  }

  stop(): void {
    this.world = null
    this.source.world = null
  }

  /** tick the attract sim — the same system stack as a real run, minus shell state */
  update(dt: number): void {
    const w = this.world
    if (!w) return
    this.loopT += dt
    w.levelTime += dt
    snapshotPositions(w)
    playerSystem(w, dt)
    aiSystem(w, dt)
    generatorSystem(w, dt)
    projectileSystem(w, dt)
    pickupSystem(w, dt)
    attritionSystem(w, dt)
    bossSystem(w, dt)
    fxSystem(w, dt)
    w.sweep()

    if (w.players().length === 0) this.deadT += dt
    const anyHostiles = w.entities.some(hostile)
    if (this.deadT > this.tuning.attract.deathLinger || !anyHostiles || w.cleared || this.loopT > this.tuning.attract.loopSeconds) {
      this.start()
    }
  }
}
